import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames/bind';
import moment from 'moment';
import { isMomentPropType } from '../../lib/customPropTypes';
import { convertToMomentObject, isDayDisabled, timezoneToOffset } from '../../lib/dateUtils';
import Calendar from '../Calendar/Calendar';
import Input from '../Input/Input';
import PopupMenu from '../PopupMenu/PopupMenu';
import Gravity from '../../constants/Gravity';

/**
 * `DateInput`is an input field that displays a calendar when it gets the input focus.
 *
 * Any child components will be displayed at the bottom of the calendar popup, but only if the todayButton prop is not set to true.
 * An optional notice can be displayed below the input field. See {@link Notice} for the different types.
 *
 * @property {string} dateFormat The format of the selected date to be displayed in the input field Ex. "DD/MM/YYYY".
 * Defaults to the format determined by the *locale* prop.
 * @property {string} locale Locale string for the selected dates display format
 * @property {boolean} disabled If this is set to `true` the input field will be read only and the calendar cannot be displayed.
 * @property {node} errorMessage An error {@link Notice} that will be shown below the component. Defaults to no Notice.
 * @property {node} infoMessage An info Notice that will be shown below the component. Defaults to no Notice.
 * @property {boolean} isClearable Enables a clear button in the input field. When clicked the date is set to `null`.
 * @property {boolean} locked The input field will be disabled and show a small lock icon to the right of the input field.
 * @property {boolean} readOnly The input field is read only, but you can select a date from the calendar.
 * @property {function} onChange Returns the selected moment object.
 * @property {function} onClearNotice Callback that is called when the user dismiss the optional Notice. * @property {string} gravity The gravity of the calendar popup. See {@link PopupMenu} for valid values.
 * @property {function} onCloseMenu Callback that is called on menu close.
 * @property {function} onCloseCalendar Callback that is called on calendar close.
 * @property {boolean} showCalendar Decides whether the calendar popup should be open. Note that if you want the
 * calendar to close on normal actions, this must ble handled through callbacks that changes the value in show Calendar
 * in onCloseMenu and onCloseCalendar.
 * @property {node} successMessage A success Notice that will be shown below the component. Defaults to no Notice.
 * @property {node} warningMessage A warning Notice that will be shown below the component. Defaults to no Notice.
 *
 * @see Calendar, DateTimeInput, Input, Notice, PopupMenu, TimeInput
 */

export default class DateInput extends Component {

  static displayName = 'DateInput';

  static propTypes = {
    className: PropTypes.string,
    dateFormat: PropTypes.string,
    disabled: PropTypes.bool,
    // errors: PropTypes.object,
    gravity: PropTypes.string,
    isClearable: PropTypes.bool,
    errorMessage: PropTypes.node,
    warningMessage: PropTypes.node,
    infoMessage: PropTypes.node,
    label: PropTypes.string,
    locale: PropTypes.string,
    locked: PropTypes.bool,
    onBlur: PropTypes.func,
    onClick: PropTypes.func,
    onFocus: PropTypes.func,
    onChange: PropTypes.func.isRequired,
    onCloseMenu: PropTypes.func,
    onCloseCalendar: PropTypes.func,
    placeholder: PropTypes.string,
    readOnly: PropTypes.bool,
    required: PropTypes.bool,
    showCalendar: PropTypes.bool,
    showIcon: PropTypes.bool,
    selected: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.instanceOf(Date),
      isMomentPropType,
    ]),
    successMessage: PropTypes.node,
    utcOffset: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number]
    ),
    onTodayButtonClick: PropTypes.func,
  };

  static defaultProps = {
    dateFormat: 'L',
    gravity: Gravity.SOUTH,
    locale: moment().locale(),
    utcOffset: moment.utc().utcOffset(),
    isClearable: true
  };

  static formatDate(props, selected) {
    return selected ?
      selected
        .locale(props.locale || moment.locale())
        .utcOffset(timezoneToOffset(props.utcOffset))
        .format(props.dateFormat) : '';
  }

  constructor(props, context) {
    super(props, context);
    this.onChangeDate = this.onChangeDate.bind(this);
    this.onCloseCalendar = this.onCloseCalendar.bind(this);
    this.onInputChange = this.onInputChange.bind(this);
    this.onInputClick = this.onInputClick.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onFocus = this.onFocus.bind(this);
    this.renderPopoverCalendar = this.renderPopoverCalendar.bind(this);
    this.renderInput = this.renderInput.bind(this);
    this.renderCalendar = this.renderCalendar.bind(this);
    this.onCloseMenu = this.onCloseMenu.bind(this);

    const selected = convertToMomentObject(props.selected);
    this.state = {
      value: DateInput.formatDate(this.props, selected),
      validInput: true,
      anchor: null,
      offset: 0,
      arrowSize: 10,
      selected,
    };
  }

  componentDidMount() {
    if (this.props.showCalendar === true) {
      this.focusInput();
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.showCalendar === true && this.state.anchor === null) {
      this.focusInput();
    }
    const newSelected = convertToMomentObject(this.props.selected);
    const prevSelected = convertToMomentObject(prevProps.selected);
    if (DateInput.formatDate(this.props, newSelected) !== DateInput.formatDate(prevProps, prevSelected)) {
      this.setState({
        selected: newSelected,
        value: DateInput.formatDate(this.props, newSelected),
        validInput: true
      });
    }
  }

  onFocus(e) {
    if (this.props.onFocus) {
      this.props.onFocus(e);
    }
  }

  onBlur = e => {
    if (this.props.onBlur) {
      this.props.onBlur(e);
    }
  };

  onCloseCalendar(e) {
    this.setState({ anchor: null });
    if (this.props.onCloseCalendar) {
      this.props.onCloseCalendar(e);
    }
  }

  onChangeDate(date) {
    if (this.props.onChange) {
      this.props.onChange(date);
    }
  }

  onCloseMenu(e) {
    this.setState({ anchor: null });
    if (this.props.onCloseMenu) {
      this.props.onCloseMenu(e);
    }
  }

  onInputClick = (event) => {
    this.setState({
      anchor: event.target,
    });
    if (this.props.onClick) {
      this.props.onClick(event);
    }
  };

  onInputChange(event) {
    if (event.type === 'clear') {
      this.onChangeDate(null);
    } else if (!event.isDefaultPrevented()) {
      const value = event.target.value;
      let validInput = false;
      const formats = [this.props.dateFormat,
        'DD/MM/YYYY', 'DD.MM.YYYY', 'DD-MM-YYYY',
        'YYYY-MM-DD', 'YYYY.MM.DD', 'YYYY/MM/DD',
        'MM/DD/YYYY', 'MM-DD-YYYY', 'MM.DD.YYYY'];
      const inputDate = moment(value, formats, true);
      if (inputDate.isValid() && !isDayDisabled(inputDate, this.props)) {
        validInput = true;
        const selectedDate = this.state.selected || moment.utc().utcOffset(timezoneToOffset(this.props.utcOffset));
        const timezoneDate = selectedDate.clone()
          .year(inputDate.year())
          .month(inputDate.month())
          .date(inputDate.date());
        this.onChangeDate(timezoneDate);
        this.setState({ selected: timezoneDate })
      } else if (value === '') {
        validInput = true;
        this.onChangeDate(null);
      }
      this.setState({ value, validInput });
    }
  }

  onChange(date) {
    this.setState({ anchor: null, selected: date });
    if (this.props.onChange) {
      this.props.onChange(date);
    }
  }
  focusInput = () => this.setState({ anchor: this.inputField.refs.input });

  hasLightHeader = () => this.props.className && this.props.className.indexOf('light-header') !== -1;

  renderInput() {
    const {disabled, errorMessage, infoMessage, isClearable, label, locked, onClearNotice, placeholder, readOnly, required, showIcon, successMessage, warningMessage} = this.props;
    const cn = cx(
      'dateinput-wrapper',
      { 'dateinput-wrapper--invalid': !this.state.validInput },
      {'has-errors': errorMessage || warningMessage || successMessage || infoMessage},
      );

    return (
      <div className={cn}>
        <Input
          ref={(c) => { this.inputField = c; }}
          tabIndex="0"
          className="dateinput-field"
          errorMessage={errorMessage}
          warningMessage={warningMessage}
          successMessage={successMessage}
          infoMessage={infoMessage}
          disabled={disabled}
          readOnly={readOnly}
          value={this.state.value}
          label={label}
          locked={locked}
          onBlur={this.onBlur}
          onFocus={this.onFocus}
          onChange={this.onInputChange}
          onClearNotice={onClearNotice}
          onClick={this.onInputClick}
          placeholder={placeholder}
          required={required}
          showClear={isClearable}
          clearable={isClearable}
          iconName={showIcon ? 'calendar' : null}/>
      </div>);
  }

  renderCalendar() {
    return (<Calendar
      {...this.props}
      className={cx({ 'light-header' : this.hasLightHeader() })}
      onClose={this.onCloseCalendar}
      onSelect={this.onChange}
      selected={this.state.selected}
      onBlur={this.props.onBlur}
      isClearable={false}
    />);
  }

  renderPopoverCalendar() {
    const { anchor, position, offset, arrowSize } = this.state;
    let popup;

    if (anchor || position) {
      const calendar = (
        <div data-preventmenuclose>{this.renderCalendar()}</div>
      );
      popup = (
        <PopupMenu anchor={anchor} gravity={this.props.gravity}
          onClose={this.onCloseMenu}
          offset={offset} arrowSize={arrowSize} customContent={true}>
          {calendar}
        </PopupMenu>
      );
    }
    return (
      <div>
        {this.renderInput()}
        {popup}
      </div>
    );
  }

  render() {
    return (
      <div className={cx('core-dateinput', this.props.className)}>
        {this.renderPopoverCalendar()}
      </div>
    );
  }

}
